import { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { vi } from 'date-fns/locale';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useMyUpcomingMatches, useSendMatchInvitation } from '@/hooks/useMatchInvitations';
import { Skeleton } from '@/components/ui/skeleton';
import { MapPin, Calendar, Clock, Users, AlertTriangle } from 'lucide-react';
import { SportBadge } from '@/components/SportBadge';
import { SkillLevelBadge, SkillLevel, SKILL_LEVEL_LABELS } from '@/components/SkillLevelBadge';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';

interface InviteToMatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inviteeId: string;
  inviteeName: string;
}

export function InviteToMatchDialog({
  open,
  onOpenChange,
  inviteeId,
  inviteeName,
}: InviteToMatchDialogProps) {
  const { profile } = useAuth();
  const [selectedMatchId, setSelectedMatchId] = useState<string>('');
  const [message, setMessage] = useState('');
  const [team, setTeam] = useState<string>('');

  const { data: matches, isLoading } = useMyUpcomingMatches();
  const sendInvitation = useSendMatchInvitation();

  // Fetch invitee's skill level
  const { data: inviteeProfile } = useQuery({
    queryKey: ['invitee-profile', inviteeId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('skill_level')
        .eq('id', inviteeId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!inviteeId && open,
  });

  const inviteeSkillLevel = (inviteeProfile as any)?.skill_level as SkillLevel | undefined;
  const creatorSkillLevel = (profile as any)?.skill_level as SkillLevel | undefined;

  const selectedMatch = matches?.find((m) => m.id === selectedMatchId);
  
  // Check if selected match is ranked and skill levels don't match
  const isRankedMismatch = useMemo(() => {
    if (!selectedMatch?.is_ranked) return false;
    if (!inviteeSkillLevel || !creatorSkillLevel) return false;
    return inviteeSkillLevel !== creatorSkillLevel;
  }, [selectedMatch, inviteeSkillLevel, creatorSkillLevel]);

  // Filter matches - for ranked matches, only show if skill levels match
  const availableMatches = useMemo(() => {
    if (!matches) return [];
    
    return matches.map(match => ({
      ...match,
      skillLevelMismatch: match.is_ranked && inviteeSkillLevel && creatorSkillLevel && inviteeSkillLevel !== creatorSkillLevel,
    }));
  }, [matches, inviteeSkillLevel, creatorSkillLevel]);

  const handleSubmit = () => {
    if (!selectedMatchId) return;
    
    if (isRankedMismatch) {
      return; // Prevent submission if skill levels don't match for ranked match
    }

    sendInvitation.mutate(
      {
        matchId: selectedMatchId,
        inviteeId,
        message: message || undefined,
        team: team ? parseInt(team) : undefined,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          setSelectedMatchId('');
          setMessage('');
          setTeam('');
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Mời tham gia trận đấu</DialogTitle>
          <DialogDescription>
            Chọn trận đấu để mời <strong>{inviteeName}</strong> tham gia
            {inviteeSkillLevel && (
              <span className="flex items-center gap-2 mt-2">
                Trình độ: <SkillLevelBadge level={inviteeSkillLevel} size="sm" />
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : !matches?.length ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>Bạn chưa có trận đấu sắp tới nào.</p>
              <p className="text-sm mt-1">Hãy tạo trận đấu mới để mời người chơi.</p>
            </div>
          ) : (
            <>
              <RadioGroup value={selectedMatchId} onValueChange={setSelectedMatchId}>
                <div className="space-y-3">
                  {availableMatches.map((match) => (
                    <div
                      key={match.id}
                      className={`relative flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedMatchId === match.id
                          ? 'border-primary bg-primary/5'
                          : match.skillLevelMismatch
                          ? 'border-destructive/50 bg-destructive/5 opacity-60'
                          : 'border-border hover:bg-muted/50'
                      }`}
                      onClick={() => setSelectedMatchId(match.id)}
                    >
                      <RadioGroupItem value={match.id} id={match.id} className="mt-1" />
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <SportBadge sport={match.sport} size="sm" />
                          <span className="text-xs text-muted-foreground capitalize">
                            {match.match_type}
                          </span>
                          {match.is_ranked && (
                            <span className="text-xs bg-amber-500/20 text-amber-600 px-1.5 py-0.5 rounded">
                              Xếp hạng
                            </span>
                          )}
                          {match.skillLevelMismatch && (
                            <span className="text-xs bg-destructive/20 text-destructive px-1.5 py-0.5 rounded flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              Khác trình độ
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {format(new Date(match.date), 'dd/MM', { locale: vi })}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {match.time.slice(0, 5)}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <MapPin className="w-3 h-3" />
                          <span className="truncate">{match.location}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </RadioGroup>
              
              {/* Skill level mismatch warning */}
              {isRankedMismatch && (
                <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-destructive">Không thể mời cho trận rank</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {inviteeName} có trình độ {inviteeSkillLevel && SKILL_LEVEL_LABELS[inviteeSkillLevel]}, 
                      khác với trình độ {creatorSkillLevel && SKILL_LEVEL_LABELS[creatorSkillLevel]} của bạn. 
                      Trận đấu rank chỉ cho phép người chơi cùng trình độ.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}

          {selectedMatch?.match_type === 'team' && (
            <div className="space-y-2">
              <Label>Chọn đội</Label>
              <RadioGroup value={team} onValueChange={setTeam}>
                <div className="flex gap-4">
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="1" id="team1" />
                    <Label htmlFor="team1" className="cursor-pointer">
                      <Users className="w-4 h-4 inline mr-1" />
                      Đội 1
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="2" id="team2" />
                    <Label htmlFor="team2" className="cursor-pointer">
                      <Users className="w-4 h-4 inline mr-1" />
                      Đội 2
                    </Label>
                  </div>
                </div>
              </RadioGroup>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="message">Lời nhắn (tùy chọn)</Label>
            <Textarea
              id="message"
              placeholder="Viết lời nhắn cho người chơi..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Hủy
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!selectedMatchId || sendInvitation.isPending || isRankedMismatch}
            >
              {sendInvitation.isPending ? 'Đang gửi...' : 'Gửi lời mời'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
